import { z } from 'zod';
import { normalizeEmailDomain } from './tenant-email-domain.js';
/**
 * WHO IS TOLD ABOUT EVERY CHECK-IN AT ONE LOCATION, regardless of host (handoff-0017 §2):
 * reception, security, a shared inbox. The list belongs to exactly one location — a child
 * location does not inherit its parent's.
 *
 * `PERSON` — a directory person, reached at their own `Person.email` and/or `Person.phone`.
 * `EMAIL` — a free-form address, EMAIL ONLY. Its domain must be on the tenant's allowed list
 * (`tenant-email-domain.ts`). There is no free-form phone entry at all: every text goes to a
 * person, so the SMS opt-out (issue #83) always applies.
 *
 * Must stay identical — same members, SAME ORDER — to the `LocationNotificationRecipientKind`
 * Prisma enum (ADR-0011), proven by packages/db/src/notification-enums.test.ts.
 */
export const LOCATION_NOTIFICATION_RECIPIENT_KINDS = ['PERSON', 'EMAIL'];
export const locationNotificationRecipientKindSchema = z.enum(LOCATION_NOTIFICATION_RECIPIENT_KINDS);
/**
 * A free-form recipient address: trimmed, lower-cased, its domain punycoded, then held to the
 * usual address shape. Lower-cased so the per-location uniqueness is case-insensitive.
 */
export const notificationRecipientEmailSchema = z
    .string()
    .max(320)
    .transform((value) => {
    const trimmed = value.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0)
        return trimmed;
    return `${trimmed.slice(0, at)}@${normalizeEmailDomain(trimmed.slice(at + 1))}`;
})
    .pipe(z.string().email().max(254));
const triggers = {
    onArrival: z.boolean().default(true),
    onCheckout: z.boolean().default(true),
};
/**
 * `POST /api/web/visitor/locations/:locationId/notification-recipients` body. A discriminated
 * union on `kind`, each member `.strict()`: an EMAIL entry that names `viaSms` or `personId` is
 * a 400 from the schema, never a silently-ignored field.
 */
export const createLocationNotificationRecipientSchema = z
    .discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('PERSON'),
        personId: z.string().min(1).max(64),
        viaEmail: z.boolean().default(true),
        viaSms: z.boolean().default(false),
        ...triggers,
    })
        .strict(),
    z
        .object({
        kind: z.literal('EMAIL'),
        email: notificationRecipientEmailSchema,
        ...triggers,
    })
        .strict(),
])
    .superRefine((value, ctx) => {
    if (value.kind === 'PERSON' && !value.viaEmail && !value.viaSms) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['viaEmail'],
            message: 'choose email, text, or both',
        });
    }
    if (!value.onArrival && !value.onCheckout) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['onArrival'],
            message: 'choose check-in, check-out, or both',
        });
    }
});
/**
 * `PATCH .../notification-recipients/:id` body — the toggles only. Who the entry names (its
 * kind, person or address) never changes: remove it and add another. `viaSms: true` on an
 * EMAIL entry, and an edit that leaves no channel or no trigger on, are refused by the service
 * (`NOTIFICATION_RECIPIENT_INVALID_CHANNELS`), since only it knows the stored row.
 */
export const updateLocationNotificationRecipientSchema = z
    .object({
    viaEmail: z.boolean().optional(),
    viaSms: z.boolean().optional(),
    onArrival: z.boolean().optional(),
    onCheckout: z.boolean().optional(),
})
    .strict()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'nothing to update',
});
/**
 * One list entry, as an administrator reads it. `personName` is the directory display name for
 * a PERSON entry (`null` for EMAIL); the person's own address and phone are NOT here — the
 * notification module resolves them at send time. `email` is the free-form address an
 * administrator typed for an EMAIL entry (`null` for PERSON).
 */
export const locationNotificationRecipientDTOSchema = z
    .object({
    id: z.string().min(1),
    locationId: z.string().min(1),
    kind: locationNotificationRecipientKindSchema,
    personId: z.string().min(1).nullable(),
    personName: z.string().nullable(),
    email: z.string().nullable(),
    viaEmail: z.boolean(),
    viaSms: z.boolean(),
    onArrival: z.boolean(),
    onCheckout: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=location-notification-recipient.js.map